/**
 * Screens Section - Kiosk Settings
 * Connection Required, Hide Stop Button, Tariff Display, Session Timeout, Idle Screen, Pre-Auth Selection
 */

import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useKioskSettingsStore } from '../../../stores/kiosk-settings-store';
import { ConnectedActionEnum, PHONE_PATTERN_OPTIONS, StopSessionModeEnum } from '../../../types/kiosk-settings-types';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Switch } from '@/core/components/ui/switch';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { PRE_AUTH_MAX_DIGITS, PRE_AUTH_SLOTS, getCurrencySymbol, getPreAuthMinimum, validatePreAuth } from '../../../utils/pre-auth-validation';

function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ScreensSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  hideTariffDisplay?: boolean;
  /** Machine-level only — live Lynx Default Credit (ceiling + currency for the pre-auth presets) */
  defaultCredit?: { amount: number | null; currencyCode: string | null; currencySign?: string | null } | null;
}

export const ScreensSection = ({ isOpen, onToggle, hideTariffDisplay = false, defaultCredit = null }: ScreensSectionProps) => {
  const { t, i18n } = useTranslation('assignEv');
  const { settings, updateField, updateNestedField } = useKioskSettingsStore();

  if (!settings) return null;

  const connectedAction = settings.connectedAction || {};
  const isTimeChecked = connectedAction.time != null && (connectedAction.time as number) > 0;
  const connectedActionTimeSeconds = isTimeChecked
    ? Math.round((connectedAction.time as number) / 1000)
    : 10;
  const isPricing = connectedAction.type === ConnectedActionEnum.PRICING;
  const connStatusDisabled = settings.connectedStatusSupport === false;
  const connStatusDisabledNote = connStatusDisabled
    ? " Disabled because 'Connected Status Support' is turned off in General Settings."
    : '';
  const licensePlate = settings.licensePlate || {};
  const phoneNumber = settings.phoneNumber || {};
  const preAuth = settings.preAuth ?? { enabled: false, values: [] };
  const preAuthValues = Array.from({ length: PRE_AUTH_SLOTS }, (_, i) => preAuth.values?.[i] ?? null);
  const preAuthCeiling = defaultCredit?.amount ?? null;
  const preAuthValidation = validatePreAuth(preAuth, preAuthCeiling);
  const currencySymbol = defaultCredit?.currencySign || getCurrencySymbol(defaultCredit?.currencyCode);
  const formatAmount = (amount: number | null) =>
    amount != null ? `${currencySymbol}${new Intl.NumberFormat(i18n.language).format(amount)}` : '';
  const formattedCeiling = formatAmount(preAuthCeiling);
  const formattedMinimum = formatAmount(getPreAuthMinimum(preAuthCeiling));
  const setPreAuthValue = (index: number, raw: string) => {
    if (!/^\d*$/.test(raw)) return; // digits only — no letters, minus or decimals
    const values = [...preAuthValues];
    values[index] = raw === '' ? null : Number(raw);
    updateField('preAuth', { ...preAuth, values });
  };

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('kiosk.screensSection.title')}</h3>
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-4 py-3 bg-white space-y-8">
          {/* ===== SCREEN BEHAVIOR ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('kiosk.screensSection.subsections.screenBehavior')}
            </h4>
            <div className="flex gap-8 items-center flex-wrap">
              <div className="flex items-center gap-3">
                <Switch
                  id="connectionRequired"
                  checked={settings.connectionRequired || false}
                  onCheckedChange={(checked) =>
                    updateField('connectionRequired', checked)
                  }
                  disabled={connStatusDisabled}
                />
                <Label
                  htmlFor="connectionRequired"
                  className="text-sm cursor-pointer"
                >
                  {t('kiosk.screensSection.fields.startButtonConnectionOnly')}
                </Label>
                <InfoTooltip text={"Show the Start button only after the driver connects the car to the charging station." + connStatusDisabledNote} />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="hideStopButton"
                  checked={settings.hideStopButton || false}
                  onCheckedChange={(checked) =>
                    updateField('hideStopButton', checked)
                  }
                />
                <Label
                  htmlFor="hideStopButton"
                  className="text-sm cursor-pointer"
                >
                  {t('kiosk.screensSection.fields.hideStopButton')}
                </Label>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="connectedSorting"
                  checked={settings.isConnectedSorting || false}
                  onCheckedChange={(checked) =>
                    updateField('isConnectedSorting', checked)
                  }
                  disabled={connStatusDisabled}
                />
                <Label
                  htmlFor="connectedSorting"
                  className="text-sm cursor-pointer"
                >
                  {t('kiosk.generalSection.fields.dynamicConnectors')}
                </Label>
                <InfoTooltip text={"Sort connectors by connection status on the kiosk home screen." + connStatusDisabledNote} />
              </div>
            </div>

          </div>

          {/* ===== DISPLAY SETTINGS ===== */}
          {!hideTariffDisplay && <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('kiosk.screensSection.subsections.displaySettings')}
            </h4>
            <div className="mb-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  {t('kiosk.screensSection.fields.tariffDisplay')}
                </Label>
                <InfoTooltip text="Choose how tariff time periods appear on terminals: Relative shows a 24-hour window starting from current time. Exact shows tariff periods exactly as configured in CSMS." />
              </div>
              <div className="inline-flex rounded-full border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
                <button
                  type="button"
                  onClick={() => updateField('tariff24HoursDisplayNonRelative', false)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    !settings.tariff24HoursDisplayNonRelative
                      ? 'bg-yellow-400 text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Relative
                </button>
                <button
                  type="button"
                  onClick={() => updateField('tariff24HoursDisplayNonRelative', true)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    settings.tariff24HoursDisplayNonRelative
                      ? 'bg-yellow-400 text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Exact
                </button>
              </div>
            </div>
            {/* Waiting Status Timeout — operator-only field (hidden on kiosk)
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Label className="text-xs font-medium text-gray-600">
                  Waiting Status Timeout
                </Label>
                <InfoTooltip text="After a remote start, wait at least x seconds before returning to available/connected status if session did not start." />
              </div>
              <Input
                type="number"
                min="30"
                max="300"
                value={sessionTimeoutSeconds || ''}
                onChange={(e) => {
                  const seconds = parseInt(e.target.value);
                  updateField('sessionTimeout', seconds ? seconds * 1000 : null);
                }}
                className="bg-white border-gray-300 w-28"
              />
            </div>
            */}
          </div>}

          {/* ===== FIRST SCREEN AFTER PLUG-IN ===== */}
          <div>
            <div className="flex items-center gap-1.5 mb-4 pb-2 border-b border-gray-200">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                First Screen After Plug-In
              </h4>
              <InfoTooltip text={"Select which screen appears first after the driver plugs in the charging cable." + connStatusDisabledNote} />
            </div>
            <div className={`space-y-3 ${connStatusDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Label className="text-xs font-medium text-gray-600">Type</Label>
                  <InfoTooltip text="Select which screen should appear after the system returns from idle mode." />
                </div>
                <div className="inline-flex rounded-full border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
                  {[ConnectedActionEnum.DISABLED, ConnectedActionEnum.PAYMENT, ConnectedActionEnum.PRICING].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        updateField('connectedAction', {
                          ...connectedAction,
                          type: option,
                          time: option !== ConnectedActionEnum.PRICING
                            ? 0
                            : (connectedAction.time != null && connectedAction.time > 0 ? connectedAction.time : -1000),
                        });
                      }}
                      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        connectedAction.type === option
                          ? 'bg-yellow-400 text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {isPricing && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="connectedActionTimeEnabled"
                      checked={isTimeChecked}
                      onCheckedChange={(checked) => {
                        updateNestedField('connectedAction', 'time', checked ? 10000 : -1000);
                      }}
                    />
                    <Label htmlFor="connectedActionTimeEnabled" className="text-xs font-medium text-gray-600 cursor-pointer">
                      Time (in seconds)
                    </Label>
                    <InfoTooltip text="How long the pricing screen displays before advancing automatically to the payment screen. (10–30 s)" />
                  </div>
                  {isTimeChecked && (
                    <Input
                      type="number"
                      min={5}
                      max={30}
                      step={5}
                      value={connectedActionTimeSeconds}
                      onKeyDown={(e) => {
                        if (!['ArrowUp', 'ArrowDown', 'Tab'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const seconds = parseInt(e.target.value);
                        if (!isNaN(seconds)) {
                          const clamped = Math.min(30, Math.max(5, Math.round(seconds / 5) * 5));
                          updateNestedField('connectedAction', 'time', clamped * 1000);
                        }
                      }}
                      className="bg-white border-gray-300 w-28"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ===== STOP SESSION MODE ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              Stop Session
            </h4>
            <div className="flex gap-6 items-start flex-wrap">
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Label className="text-xs font-medium text-gray-600">Mode</Label>
                  <InfoTooltip text={t('kiosk.screensSection.fields.stopSessionModeTooltip')} />
                </div>
                <Select
                  value={String(settings.stopSessionMode ?? StopSessionModeEnum.STOP_WITH_CARD)}
                  onValueChange={(value) => updateField('stopSessionMode', Number(value) as StopSessionModeEnum)}
                >
                  <SelectTrigger className="bg-white border-gray-300 w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent avoidCollisions={false}>
                    <SelectItem value={String(StopSessionModeEnum.STOP_WITH_CARD)}>
                      {t(`kiosk.screensSection.fields.stopSessionMode_${StopSessionModeEnum.STOP_WITH_CARD}`)}
                    </SelectItem>
                    <SelectItem value={String(StopSessionModeEnum.STOP_WITHOUT_CARD_INSTANT)}>
                      {t(`kiosk.screensSection.fields.stopSessionMode_${StopSessionModeEnum.STOP_WITHOUT_CARD_INSTANT}`)}
                    </SelectItem>
                    <SelectItem value={String(StopSessionModeEnum.STOP_WITHOUT_CARD_CONFIRM)}>
                      {t(`kiosk.screensSection.fields.stopSessionMode_${StopSessionModeEnum.STOP_WITHOUT_CARD_CONFIRM}`)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ===== LICENSE PLATE ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('kiosk.licensePlateSection.title')}
            </h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="showLicensePlate"
                  checked={licensePlate.enabled || false}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      updateNestedField('licensePlate', 'enabled', true);
                    } else {
                      updateField('licensePlate', { enabled: false, isMandatory: false, allowedCharacters: undefined });
                    }
                  }}
                />
                <Label htmlFor="showLicensePlate" className="text-sm cursor-pointer">
                  {t('kiosk.licensePlateSection.fields.enableCapture')}
                </Label>
                <InfoTooltip text="When enabled, drivers will be prompted to enter their vehicle license plate number before starting a charging session." />
              </div>
              {licensePlate.enabled && (
                <>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="licensePlateMandatory"
                      checked={licensePlate.isMandatory || false}
                      onCheckedChange={(checked) => updateNestedField('licensePlate', 'isMandatory', checked)}
                    />
                    <Label htmlFor="licensePlateMandatory" className="text-sm cursor-pointer">
                      {t('kiosk.licensePlateSection.fields.required')}
                    </Label>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-600 mb-2 block">
                      {t('kiosk.licensePlateSection.fields.allowedCharacters')}
                    </Label>
                    <Select
                      value={licensePlate.allowedCharacters === 1 || licensePlate.allowedCharacters === 2 ? licensePlate.allowedCharacters.toString() : ''}
                      onValueChange={(value) => updateNestedField('licensePlate', 'allowedCharacters', parseInt(value))}
                    >
                      <SelectTrigger className="bg-white border-gray-300 w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent avoidCollisions={false}>
                        <SelectItem value="1">Digits &amp; Characters</SelectItem>
                        <SelectItem value="2">Digits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ===== PHONE NUMBER ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('kiosk.phoneNumberSection.title')}
            </h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="showPhoneNumber"
                  checked={phoneNumber.enabled || false}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      updateNestedField('phoneNumber', 'enabled', true);
                    } else {
                      updateField('phoneNumber', { enabled: false, isMandatory: false });
                    }
                  }}
                />
                <Label htmlFor="showPhoneNumber" className="text-sm cursor-pointer">
                  {t('kiosk.phoneNumberSection.fields.enableCapture')}
                </Label>
                <InfoTooltip text="When enabled, drivers will be prompted to enter their phone number before starting a charging session." />
              </div>
              {phoneNumber.enabled && (
                <>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="phoneNumberMandatory"
                      checked={phoneNumber.isMandatory || false}
                      onCheckedChange={(checked) => updateNestedField('phoneNumber', 'isMandatory', checked)}
                    />
                    <Label htmlFor="phoneNumberMandatory" className="text-sm cursor-pointer">
                      {t('kiosk.phoneNumberSection.fields.required')}
                    </Label>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-600 mb-2 block">
                      Phone Pattern
                    </Label>
                    <Select
                      value={phoneNumber.phonePattern ?? PHONE_PATTERN_OPTIONS.NONE}
                      onValueChange={(value) => updateNestedField('phoneNumber', 'phonePattern', value)}
                    >
                      <SelectTrigger className="bg-white border-gray-300 w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent avoidCollisions={false}>
                        <SelectItem value={PHONE_PATTERN_OPTIONS.NONE}>None</SelectItem>
                        <SelectItem value={PHONE_PATTERN_OPTIONS.ISRAEL}>Israel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ===== PRE-AUTH SELECTION ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('kiosk.preAuthSection.title')}
            </h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="preAuthEnabled"
                  checked={preAuth.enabled || false}
                  // Values are kept when turning off
                  onCheckedChange={(checked) => updateField('preAuth', { ...preAuth, values: preAuthValues, enabled: checked })}
                />
                <Label htmlFor="preAuthEnabled" className="text-sm cursor-pointer">
                  {t('kiosk.preAuthSection.fields.enable')}
                </Label>
                <InfoTooltip text={t('kiosk.preAuthSection.tooltip.enable')} />
              </div>
              {/* Also shown while off if a half-filled set blocks save, so the errors are visible */}
              {(preAuth.enabled || preAuthValidation.hasError) && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="text-xs font-medium text-gray-600">
                      {t('kiosk.preAuthSection.fields.presetAmounts')}
                    </Label>
                    <InfoTooltip
                      text={preAuthCeiling != null
                        ? t('kiosk.preAuthSection.tooltip.presetsWithCeiling', { amount: formattedCeiling, min: formattedMinimum })
                        : t('kiosk.preAuthSection.tooltip.presets')}
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {preAuthValues.map((value, i) => {
                      const slotError = preAuthValidation.slotErrors[i];
                      return (
                        <div key={i}>
                          <div className="relative">
                            {currencySymbol && (
                              <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                                {currencySymbol}
                              </span>
                            )}
                            <Input
                              type="text"
                              inputMode="numeric"
                              maxLength={PRE_AUTH_MAX_DIGITS}
                              value={value ?? ''}
                              onChange={(e) => setPreAuthValue(i, e.target.value.trim())}
                              placeholder={t('kiosk.preAuthSection.fields.presetPlaceholder', { index: i + 1 })}
                              aria-invalid={!!slotError}
                              className={`bg-white ${currencySymbol ? 'ps-8' : ''} ${slotError ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
                            />
                          </div>
                          {slotError && (
                            <p className="text-xs text-red-600 mt-1">
                              {t(`kiosk.preAuthSection.errors.${slotError}`, { amount: formattedCeiling, min: formattedMinimum })}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
